import { useState } from "react";
import { XMarkIcon, PlusIcon } from "@heroicons/react/24/outline";
import { classNames } from "@via/utils/helpers";
import type { Conversation } from "@via/utils/interfaces";

interface ConversationTabsProps {
  conversations: Conversation[];
  activeConversationId: string | null;
  onSelectConversation: (id: string) => void;
  onCreateNew: () => void;
  onCloseTab: (id: string) => void;
  onRenameConversation: (id: string, title: string | null) => Promise<void>;
}

export function ConversationTabs({
  conversations,
  activeConversationId,
  onSelectConversation,
  onCreateNew,
  onCloseTab,
  onRenameConversation,
}: ConversationTabsProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState("");
  const canCreateNew = conversations.length < 6;

  const getTabTitle = (conversation: Conversation): string => {
    if (conversation.isPending) {
      return "New Conversation";
    }
    return conversation.title || "Untitled Conversation";
  };

  const startEditing = (conversation: Conversation) => {
    setEditingId(conversation.id);
    setDraftTitle(getTabTitle(conversation));
  };

  const cancelEditing = () => {
    setEditingId(null);
    setDraftTitle("");
  };

  const commitEditing = async () => {
    if (!editingId) return;
    const trimmed = draftTitle.trim();
    const nextTitle = trimmed === "" ? null : trimmed;
    await onRenameConversation(editingId, nextTitle);
    cancelEditing();
  };

  const handleClose = (e: React.MouseEvent, conversationId: string) => {
    e.stopPropagation();
    onCloseTab(conversationId);
  };

  return (
    <div className="border-b border-gray-200 bg-white">
      <div className="flex flex-wrap items-center gap-1 px-2 py-1">
        {conversations.map(conversation => {
          const isActive = conversation.id === activeConversationId;
          const title = getTabTitle(conversation);
          const truncatedTitle =
            title.length > 20 ? `${title.substring(0, 20)}...` : title;
          const isEditing = editingId === conversation.id;

          return (
            <button
              key={conversation.id}
              onClick={() => onSelectConversation(conversation.id)}
              onDoubleClick={() => {
                if (!conversation.isPending) {
                  startEditing(conversation);
                }
              }}
              className={classNames(
                "group relative flex items-center gap-2 px-3 py-1.5 text-xs font-medium transition-colors",
                "border-b-2 flex-shrink-0 max-w-xs",
                isActive
                  ? "border-indigo-600 text-indigo-600 bg-indigo-50/50"
                  : "border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300"
              )}
              title={title}>
              {isEditing ? (
                <input
                  value={draftTitle}
                  onChange={e => setDraftTitle(e.target.value)}
                  onBlur={commitEditing}
                  onKeyDown={e => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      void commitEditing();
                    }
                    if (e.key === "Escape") {
                      e.preventDefault();
                      cancelEditing();
                    }
                  }}
                  onClick={e => e.stopPropagation()}
                  className="max-w-[140px] rounded border border-indigo-300 px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white"
                  autoFocus
                />
              ) : (
                <span className="truncate">{truncatedTitle}</span>
              )}
              <span
                onClick={e => handleClose(e, conversation.id)}
                className={classNames(
                  "flex-shrink-0 rounded p-0.5 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer",
                  "hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                )}
                role="button"
                tabIndex={0}
                aria-label={`Close ${title}`}
                title="Close tab"
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleClose(
                      e as unknown as React.MouseEvent,
                      conversation.id
                    );
                  }
                }}>
                <XMarkIcon className="h-4 w-4" />
              </span>
            </button>
          );
        })}

        {canCreateNew && (
          <button
            onClick={onCreateNew}
            className={classNames(
              "flex items-center justify-center h-7 w-7 rounded-md border border-dashed border-gray-300",
              "text-gray-500 hover:text-indigo-600 hover:border-indigo-400 transition-colors flex-shrink-0"
            )}
            aria-label="Create new conversation">
            <PlusIcon className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
